/** The travel-map animation shown when the lift takes the player to the other office. */

/** Pixel size of `public/travel/sweden-map.png`; the animation's SVG uses it as its coordinate space. */
export const MAP_WIDTH = 860;
export const MAP_HEIGHT = 1987;

/** Where each office region sits on the map image (the city dot), placed by hand. */
export const CITY_POSITIONS: Readonly<Record<string, { x: number; y: number }>> = {
  Umeå: { x: 648, y: 716 },
  Stockholm: { x: 556, y: 1310 },
};

export const TRAVEL_MS = 4000;

export interface TravelPoint {
  readonly x: number;
  readonly y: number;
}

/** The two cities of a trip, or undefined when either region has no place on the map. */
export function travelRoute(
  from: string | undefined,
  to: string | undefined,
): { from: TravelPoint; to: TravelPoint } | undefined {
  if (!from || !to || from === to) return undefined;
  const start = CITY_POSITIONS[from];
  const end = CITY_POSITIONS[to];
  return start && end ? { from: start, to: end } : undefined;
}

/** Eases in and out so the plane takes off and lands gently. */
export function easeInOut(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** Share of the trip travelled `elapsedMs` after the start. */
export function travelProgress(elapsedMs: number, durationMs: number = TRAVEL_MS): number {
  return easeInOut(elapsedMs / durationMs);
}

/** A gentle arc between the cities: the quadratic control point, bowed to one side. */
export function arcControl(from: TravelPoint, to: TravelPoint): TravelPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return { x: (from.x + to.x) / 2 - dy * 0.2, y: (from.y + to.y) / 2 + dx * 0.2 };
}

/** Point and heading (degrees, 0 = east) on the quadratic arc at progress `t` (0..1). */
export function planeAt(
  from: TravelPoint,
  to: TravelPoint,
  t: number,
): TravelPoint & { angle: number } {
  const c = arcControl(from, to);
  const u = 1 - t;
  const x = u * u * from.x + 2 * u * t * c.x + t * t * to.x;
  const y = u * u * from.y + 2 * u * t * c.y + t * t * to.y;
  const tx = 2 * u * (c.x - from.x) + 2 * t * (to.x - c.x);
  const ty = 2 * u * (c.y - from.y) + 2 * t * (to.y - c.y);
  return { x, y, angle: (Math.atan2(ty, tx) * 180) / Math.PI };
}
